import { useState } from 'react';
import type { WorkInboxEntry } from '@ayumia/contracts';
import { text } from '../app/i18n';
import { Empty, errorView, Loading } from '../states/States';
import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';
import { Btn, PageHeader } from '../design-system/primitives';

/**
 * Boîte de travail (ADR-064 §1, §5) : ce que l'identité a pris ou peut prendre, lu par
 * `ListWorkInbox`. Le serveur filtre par assignation et par rôle ; la page ne fait que lister et
 * ouvrir une tâche. Aucun Work Item n'est propre à un Pack ici : l'inspection d'une carcasse et la
 * validation d'une découpe passent par la même page.
 */

type Scope = 'all' | 'mine' | 'available';

const SCOPES: ReadonlyArray<{ scope: Scope; label: string }> = [
  { scope: 'all', label: 'Tout' },
  { scope: 'mine', label: 'Pris par moi' },
  { scope: 'available', label: 'À prendre' },
];

/** « Brouillon enregistré à 14:32 » (le jour s'il n'est pas aujourd'hui) : champ dérivé `draft_saved_at` de la boîte. */
function draftLabel(entry: WorkInboxEntry): string | null {
  if (entry.draft_saved_at === undefined) return null;
  const at = new Date(String(entry.draft_saved_at));
  if (Number.isNaN(at.getTime())) return null;
  const sameDay = at.toDateString() === new Date().toDateString();
  return sameDay ? `Brouillon enregistré à ${at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : `Brouillon enregistré le ${at.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`;
}

function dueLabel(entry: WorkInboxEntry): string {
  if (entry.due_at === undefined) return '—';
  return new Date(String(entry.due_at)).toLocaleString('fr');
}

export function InboxPage() {
  const services = useAppServices();
  const [scope, setScope] = useState<Scope>('all');
  const [inbox, reload] = useLoad<WorkInboxEntry[]>(`inbox:${scope}`, async () => (await services.client.query<WorkInboxEntry[]>('ListWorkInbox', { scope })).data);

  return (
    <section className="ay-view" aria-labelledby="ay-inbox-title">
      <PageHeader
        titleId="ay-inbox-title"
        title="Mon travail"
        subtitle="Tâches assignées à vous ou à vos rôles."
        actions={
          <Btn variant="ghost" icon="refresh" onClick={reload}>
            Actualiser
          </Btn>
        }
      />
      <div role="tablist" className="ay-tabs__list" aria-label="Portée">
        {SCOPES.map((entry) => (
          <button key={entry.scope} type="button" role="tab" aria-selected={scope === entry.scope} onClick={() => setScope(entry.scope)}>
            {entry.label}
          </button>
        ))}
      </div>
      {inbox.status === 'loading' && <Loading />}
      {inbox.status === 'error' && errorView(inbox.error, reload)}
      {inbox.status === 'ready' &&
        (inbox.value.length === 0 ? (
          <Empty label="Rien à faire pour l’instant." />
        ) : (
          <div className="ay-table-wrap">
            <table className="ay-table">
              <thead>
                <tr>
                  <th scope="col">Tâche</th>
                  <th scope="col">Sujet</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Échéance</th>
                </tr>
              </thead>
              <tbody>
                {inbox.value.map((entry) => {
                  const id = String(entry.id);
                  const open = (): void => services.navigate({ kind: 'task', work_item_id: id });
                  return (
                    <tr
                      key={id}
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') open();
                      }}
                    >
                      <td>
                        {text(entry.title)}
                        {entry.is_approval && <span className="ay-badge ay-badge--neutral"> Décision</span>}
                        {entry.claimed_by_me && <span className="ay-badge ay-badge--success"> Pris</span>}
                        {entry.sla_state === 'breached' && <span className="ay-badge ay-badge--danger"> En retard</span>}
                        {draftLabel(entry) !== null && <span className="ay-badge ay-badge--neutral" data-testid="inbox-draft"> {draftLabel(entry)}</span>}
                      </td>
                      <td>{entry.subject === undefined ? '—' : `${entry.subject.type} ${String(entry.subject.id).slice(-6)}`}</td>
                      <td>{entry.status}</td>
                      <td>{dueLabel(entry)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
